import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import HomeIcon from "@mui/icons-material/Home";
import ParkingIcon from "@mui/icons-material/LocalParking";
import {
  Box,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { format, getTime, Locale, parseISO } from "date-fns";
import { es, fr } from "date-fns/locale";
import React, { RefObject, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Timeline, {
  CustomMarker,
  DateHeader,
  TimelineHeaders,
  TimelineMarkers,
  TodayMarker,
} from "react-calendar-timeline";
import "react-calendar-timeline/dist/style.css";
import { CsrStatus, TripWithTenant } from "../../../types";
import { csrStatusFilterColors } from "../../../utils/tripTypeHelpers";
import {
  formatDepartureDate,
  isInfinityDate,
} from "../../../utils/dateHelpers";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog/ConfirmationDialog";
import { HangarIcon } from "../../../widgets/Icons/HangarIcon";
import { RampIcon } from "../../../widgets/Icons/RampIcon";
import { ZoomControl } from "../../../widgets/ZoomControl/ZoomControl";
import { TripDetailsModal } from "../TripDetailsModal/TripDetailsModal";
import { BulkActionToolbar } from "./BulkActionToolbar";
import "./ScheduleTimeline.css";

// Locale map for date-fns
const dateFnsLocales: Record<string, Locale | undefined> = {
  es: es,
  fr: fr,
  en: undefined, // English is the default, no locale needed
};

// Label format function factory for timeline headers - uses date-fns for i18n support
const createLabelFormat = (language: string) => {
  const locale = dateFnsLocales[language];

  return (
    [startTime]: [any, any],
    unit: string,
    labelWidth: number
  ): string => {
    // Convert dayjs/moment object to JavaScript Date
    const date = startTime.toDate();

    // For day units - always use "ddd D" format (e.g., "Mon 15" / "Lun 15")
    if (unit === "day") {
      if (labelWidth < 60) {
        return format(date, "d", { locale });
      } else {
        return format(date, "EEE d", { locale });
      }
    }

    // For hour units - always use 24-hour format (e.g., "14:00")
    if (unit === "hour") {
      return format(date, "HH:00", { locale });
    }

    // For minute units
    if (unit === "minute") {
      return format(date, "HH:mm", { locale });
    }

    // For month units (e.g., "December" / "Diciembre")
    if (unit === "month") {
      return format(date, "MMMM", { locale });
    }

    // For year units
    if (unit === "year") {
      return format(date, "yyyy", { locale });
    }

    // For week units
    if (unit === "week") {
      return format(date, "'W'w", { locale });
    }

    // Default fallback
    return format(date, "P", { locale });
  };
};

interface ScheduleTimelinePresenterProps {
  // Data
  trips: TripWithTenant[];
  groups: any[];
  items: any[];
  keys: any;
  selectedTrip: TripWithTenant | null;

  // State
  modalOpen: boolean;
  placementsLoading: boolean;

  // Props from parent
  startDate: Date;
  endDate: Date;
  viewTimestamp?: Date;

  // Refs
  timelineContainerRef: RefObject<HTMLDivElement>;
  timelineRef?: RefObject<any>;

  // Callbacks
  handleItemClick: (itemId: string) => void;
  handleModalClose: () => void;
  handleNavigate: (tripId: string) => void;
  handleTripChanged: () => Promise<void>;
  handleTimeChange: (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: any
  ) => void;

  // Helper functions
  getTripLocation: (
    tripId: string
  ) => { name: string; type: "hangar" | "ramp" | "transient" } | null;
  getBorderColor: (trip: TripWithTenant, tripId: string) => string;
  getBorderWidth: (trip: TripWithTenant, tripId: string) => number;

  // Mode
  inlineMode?: boolean;

  // Zoom props
  visibleTimeStart?: number;
  visibleTimeEnd?: number;
  currentZoomLevel?: number;
  handleZoomIn?: () => void;
  handleZoomOut?: () => void;
  handleZoomReset?: () => void;

  // Multi-select props
  selectedTripIds?: Set<string>;
  enableMultiSelect?: boolean;
  showDeleteConfirmation?: boolean;
  handleBulkDelete?: () => void;
  handleBulkDeleteClick?: () => void;
  handleCancelDelete?: () => void;
  handleClearSelection?: () => void;
}

export const ScheduleTimelinePresenter: React.FC<
  ScheduleTimelinePresenterProps
> = ({
  trips,
  groups,
  items,
  keys,
  selectedTrip,
  modalOpen,
  placementsLoading,
  startDate,
  endDate,
  viewTimestamp,
  timelineContainerRef,
  timelineRef,
  handleItemClick,
  handleModalClose,
  handleNavigate,
  handleTripChanged,
  handleTimeChange,
  getTripLocation,
  getBorderColor,
  getBorderWidth,
  inlineMode = false,
  // Zoom props
  visibleTimeStart,
  visibleTimeEnd,
  currentZoomLevel = 1,
  handleZoomIn,
  handleZoomOut,
  handleZoomReset,
  // Multi-select props
  selectedTripIds = new Set(),
  enableMultiSelect = true,
  showDeleteConfirmation = false,
  handleBulkDelete,
  handleBulkDeleteClick,
  handleCancelDelete,
  handleClearSelection,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // Create locale-aware label formatter for timeline headers
  const labelFormat = useMemo(() => createLabelFormat(i18n.language), [i18n.language]);

  // Use controlled visible time or default to date range
  const defaultTimelineStart = visibleTimeStart || getTime(startDate);
  const defaultTimelineEnd = visibleTimeEnd || getTime(endDate);

  // Custom item renderer for better trip display
  const itemRenderer = ({
    item,
    itemContext,
    getItemProps,
    getResizeProps,
  }: any) => {
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();
    const trip = trips.find((t) => t.trip_id === item.id);
    const location = trip ? getTripLocation(trip.trip_id) : null;

    // Check if this trip is selected
    const isSelected = selectedTripIds.has(item.id);

    // Determine background color based on CSR status
    const csr_status = (trip?.csr_status || 'requested') as CsrStatus;
    let backgroundColor = csrStatusFilterColors[csr_status];

    // Apply selection styling
    if (isSelected) {
      // Light blue background for selected items
      backgroundColor = "#E3F2FD";
    }

    // Border color based on CSR status
    let borderColor = trip
      ? getBorderColor(trip, item.id)
      : "rgba(0,0,0,0.1)";
    let borderWidth = trip ? getBorderWidth(trip, item.id) : 1;

    // Override border for selected items
    if (isSelected) {
      borderColor = "#1976D2";
      borderWidth = 2;
    }

    // Get item props and extract key to avoid React warning
    const itemProps = getItemProps({
      style: {
        backgroundColor,
        borderTopWidth: borderWidth,
        borderRightWidth: borderWidth,
        borderBottomWidth: borderWidth,
        borderLeftWidth: borderWidth,
        borderStyle: "solid",
        borderColor: borderColor,
        borderRadius: 4,
        color: isSelected
          ? theme.palette.primary.main
          : theme.palette.text.primary,
        fontSize: 12,
        padding: "2px 4px",
        cursor: "pointer", // Show it's clickable, not draggable
        boxShadow: isSelected
          ? `0 2px 4px ${theme.palette.primary.main}4D`
          : undefined,
      },
    });
    const { key, ...restProps } = itemProps;

    return (
      <div key={key} {...restProps}>
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}

        <Tooltip
          title={
            <Box>
              <Typography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {trip?.tail_number} - {trip?.owner_manager}
                {trip?.vip_tags?.map((tag) => (
                  <Box
                    key={tag.id}
                    component="span"
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      color: tag.tag_color,
                      ml: 0.5,
                    }}
                  >
                    [{tag.tag_label}]
                  </Box>
                ))}
              </Typography>
              {location && (
                <Typography variant="caption" display="block">
                  {location.type === "hangar" ? (
                    <HomeIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  ) : location.type === "ramp" ? (
                    <ParkingIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  ) : null}
                  {t('screens.schedule.scheduleTimeline.tooltip.location', { name: location.name })}
                </Typography>
              )}
              <Typography variant="caption" display="block">
                <FlightLandIcon sx={{ fontSize: 12, mr: 0.5 }} />
                {format(parseISO(trip?.arrival_date), "MMM d, HH:mm")}
              </Typography>
              <Typography variant="caption" display="block">
                <FlightTakeoffIcon sx={{ fontSize: 12, mr: 0.5 }} />
                {formatDepartureDate(trip?.departure_date)}
              </Typography>
              {trip?.notes && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {trip.notes}
                </Typography>
              )}
            </Box>
          }
        >
          <div
            style={{
              height: itemContext.dimensions.height,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              paddingLeft: "4px",
              fontSize: 11,
              fontWeight: trip?.status === "active" ? 600 : 400,
            }}
          >
            {trip?.status === "active" && "● "}
            <span>{itemContext.title}</span>
            {location && location.type !== "transient" && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "10px",
                  fontWeight: 500,
                  marginLeft: "4px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    "& .MuiIcon-root": {
                      fontSize: "14px",
                      marginRight: "2px",
                    },
                  }}
                >
                  {location.type === "hangar" ? <HangarIcon /> : <RampIcon />}
                </Box>
                {location.name}
              </span>
            )}
          </div>
        </Tooltip>

        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
      </div>
    );
  };

  // Fix z-index layering after timeline renders
  useEffect(() => {
    const fixZIndex = () => {
      // Set trip items above grid lines
      const items = document.querySelectorAll(".rct-item");
      items.forEach((item) => {
        (item as HTMLElement).style.zIndex = "50";
      });

      // Set grid lines below trip items
      const verticalLines = document.querySelectorAll(".rct-vl");
      verticalLines.forEach((line) => {
        (line as HTMLElement).style.zIndex = "1";
      });

      const horizontalLines = document.querySelectorAll(
        ".rct-hl-even, .rct-hl-odd"
      );
      horizontalLines.forEach((line) => {
        (line as HTMLElement).style.zIndex = "1";
      });
    };

    // Fix z-index with multiple attempts to catch timeline renders
    const timers = [
      setTimeout(fixZIndex, 100),
      setTimeout(fixZIndex, 300),
      setTimeout(fixZIndex, 500),
      setTimeout(fixZIndex, 1000),
    ];

    // Also observe for dynamic changes to the timeline
    const observer = new MutationObserver(() => {
      fixZIndex();
    });

    const timelineContainer = document.querySelector(
      ".react-calendar-timeline"
    );
    if (timelineContainer) {
      observer.observe(timelineContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      observer.disconnect();
    };
  }, [trips, items]); // Re-run when trips or items change

  return (
    <>
      {placementsLoading && (
        <LinearProgress sx={{ mb: 1, height: 2 }} color="primary" />
      )}
      <Box
        ref={timelineContainerRef}
        className="schedule-timeline-container"
        sx={{
          position: "relative",
          // CSS custom properties for timeline dark mode support
          "--timeline-bg": theme.palette.background.paper,
          "--timeline-bg-default": theme.palette.background.default,
          "--timeline-border": theme.palette.divider,
          "--timeline-text-primary": theme.palette.text.primary,
          "--timeline-text-secondary": theme.palette.text.secondary,
          "--timeline-header-bg": theme.palette.background.paper,
          "--timeline-row-hover": theme.palette.action.hover,
          "--timeline-grid-line": theme.palette.divider,
          "--timeline-weekend-bg": theme.palette.action.selected,
        }}
      >
        {/* Bulk action toolbar */}
        {enableMultiSelect && handleClearSelection && (
          <BulkActionToolbar
            selectedCount={selectedTripIds.size}
            onDelete={() => handleBulkDeleteClick?.()}
            onClearSelection={handleClearSelection}
          />
        )}

        {/* Zoom controls */}
        {handleZoomIn && handleZoomOut && handleZoomReset && (
          <ZoomControl
            currentZoom={currentZoomLevel}
            increment={0.5}
            onClickPlus={() => handleZoomIn()}
            onClickMinus={() => handleZoomOut()}
            onClickReset={handleZoomReset}
            showResetButton={true}
            position="absolute"
            bottom={16}
            right={16}
          />
        )}

        {/* Empty state overlay */}
        {trips.length === 0 && (
          <Box className="timeline-empty-state">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No trips in this date range
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scroll or zoom the timeline to find trips in other time periods
            </Typography>
          </Box>
        )}
        <Timeline
          key={i18n.language}  // Force re-mount when language changes for dayjs locale
          ref={timelineRef}
          groups={groups}
          items={items}
          keys={keys}
          visibleTimeStart={defaultTimelineStart}
          visibleTimeEnd={defaultTimelineEnd}
          canMove={false}
          canResize={false}
          onItemSelect={handleItemClick}
          selected={[]} // Prevent any item from being visually selected
          buffer={1}
          lineHeight={50}
          sidebarWidth={150}
          itemTouchSendsClick={true}
          itemRenderer={itemRenderer}
          onTimeChange={handleTimeChange}
          // Enable mouse wheel zoom
          // @ts-expect-error - mouseZoom exists in runtime but not in type definitions
          mouseZoom={true}
          // Set zoom limits: 12 hours to 180 days
          minZoom={12 * 60 * 60 * 1000} // 12 hours in milliseconds
          maxZoom={180 * 24 * 60 * 60 * 1000} // 180 days in milliseconds
          // Enable drag to scroll
          dragSnap={1}
          timeSteps={{
            second: 1,
            minute: 1,
            hour: 1,
            day: 1,
            month: 1,
            year: 1,
          }}
        >
          {/* <TimelineHeaders>
            <DateHeader unit="primaryHeader" labelFormat={formatTimelineLabel(selectedSpan)} />
            <DateHeader unit="day" labelFormat={formatTimelineLabel(selectedSpan)} />
          </TimelineHeaders> */}
          <TimelineHeaders>
            <DateHeader unit="primaryHeader" labelFormat={labelFormat} />
            <DateHeader labelFormat={labelFormat} />
          </TimelineHeaders>
          <TimelineMarkers>
            {viewTimestamp ? (
              <CustomMarker date={getTime(viewTimestamp)}>
                {({ styles }) => (
                  <div
                    style={{
                      ...styles,
                      backgroundColor: "rgba(229, 62, 62, 0.7)",
                      width: "2px",
                      zIndex: 10,
                    }}
                  />
                )}
              </CustomMarker>
            ) : (
              <TodayMarker>
                {({ styles }) => (
                  <div
                    style={{
                      ...styles,
                      backgroundColor: "rgba(229, 62, 62, 0.7)",
                      width: "2px",
                      zIndex: 10,
                    }}
                  />
                )}
              </TodayMarker>
            )}
          </TimelineMarkers>
        </Timeline>
      </Box>

      {!inlineMode && (
        <TripDetailsModal
          open={modalOpen}
          trip={selectedTrip}
          trips={trips}
          onClose={handleModalClose}
          onUpdate={undefined}
          onNavigate={handleNavigate}
          onTripChanged={handleTripChanged}
          currentTimestamp={viewTimestamp || new Date()}
        />
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirmation && handleBulkDelete && handleCancelDelete && (
        <ConfirmationDialog
          title={t('screens.schedule.scheduleTimeline.deleteConfirmation.title')}
          text={t('screens.schedule.scheduleTimeline.deleteConfirmation.message', { count: selectedTripIds.size })}
          onOk={handleBulkDelete}
          onCancel={handleCancelDelete}
          onClose={handleCancelDelete}
          okText={t('screens.schedule.scheduleTimeline.deleteConfirmation.deleteButton')}
          cancelText={t('screens.schedule.scheduleTimeline.deleteConfirmation.cancelButton')}
          okButtonColor="error"
        />
      )}
    </>
  );
};
